import React, { useState, useEffect } from 'react';
import { Camera, CheckSquare, Trash2, Check, User, Maximize2, Pencil, X, Save, MessageSquare } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from './Toast';
import FilePreviewModal from './FilePreviewModal';

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const STATUS_CYCLE = { 'Open': 'In Progress', 'In Progress': 'Done', 'Done': 'Open' };
const STATUS_STYLE = {
  'Open':        'bg-red-100 text-red-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'Done':        'bg-green-100 text-green-700',
};

const PunchListView = ({ items: itemsProp, setItems: setItemsProp, onUpload, projectId }) => {
  const { showToast } = useToast();
  // Internal state so we can use functional updaters safely.
  // Parent's setItems expects a plain array (not a function updater), so we bridge here.
  const [items, setItemsLocal] = useState(() => Array.isArray(itemsProp) ? itemsProp : []);
  const setItems = (updater) => {
    setItemsLocal(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const next = typeof updater === 'function' ? updater(safePrev) : updater;
      const safeNext = Array.isArray(next) ? next : safePrev;
      setItemsProp(safeNext);
      return safeNext;
    });
  };
  // Sync when parent swaps in a new project's data
  useEffect(() => { setItemsLocal(Array.isArray(itemsProp) ? itemsProp : []); }, [itemsProp]);
  const [formData, setFormData] = useState({ description: '', location: '', priority: 'Medium', assignee: '', notes: '' });
  const [uploading, setUploading] = useState(null); // stores itemId being uploaded
  const [previewFile, setPreviewFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('All');

  const handleAdd = async () => {
    if (!formData.description.trim()) return showToast('Description required', 'error');
    if (!projectId) return showToast('No project selected', 'error');
    const tempId = Date.now();
    const optimistic = { ...formData, id: tempId, status: 'Open', photo_path: '' };
    setItems(prev => [...prev, optimistic]);
    setFormData({ description: '', location: '', priority: 'Medium', assignee: '', notes: '' });
    try {
      const saved = await api.addPunchItem(projectId, formData);
      // Replace temp id with real one from server
      setItems(prev => prev.map(i => i.id === tempId ? { ...i, id: saved.id ?? tempId } : i));
      showToast('Item added');
    } catch (e) {
      showToast('Save failed', 'error');
      setItems(prev => prev.filter(i => i.id !== tempId));
    }
  };

  const handlePhotoUpload = async (e, itemId) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(itemId);
    try {
      const res = await api.uploadFileAtomic(file, projectId, itemId, 'PunchList');
      await api.updatePunchItem(itemId, { photo_path: res.path });
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, photo_path: res.path } : i));
      showToast('Photo added');
    } catch (err) {
      showToast('Upload failed', 'error');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this punch list item?')) return;
    setItems(prev => prev.filter(i => i.id !== item.id));
    try { await api.deletePunchItem(item.id); }
    catch (e) { showToast('Delete failed', 'error'); }
  };

  const cycleStatus = async (item) => {
    const newStatus = STATUS_CYCLE[item.status] || 'Open';
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    try { await api.updatePunchItem(item.id, { status: newStatus }); }
    catch (e) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: item.status } : i));
      showToast('Update failed', 'error');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      description: item.description || '',
      location:    item.location   || '',
      priority:    item.priority   || 'Medium',
      assignee:    item.assignee   || '',
      notes:       item.notes      || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const saveEdit = async (item) => {
    if (!editData.description.trim()) return showToast('Description required', 'error');
    setSaving(true);
    try {
      const payload = {
        description: editData.description.trim(),
        location:    editData.location.trim(),
        priority:    editData.priority,
        assignee:    editData.assignee.trim(),
        notes:       editData.notes.trim(),
      };
      const res = await fetch(`/api/punchlist/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      // Merge only the editable fields, preserve photo_path, status, id etc.
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...payload } : i));
      setEditingId(null);
      setEditData({});
      showToast('Saved');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getUrl = (p) => {
    if (!p) return '';
    return p.startsWith('/api/files/') ? p : `/api/files/${p.split('/').pop()}`;
  };

  const borderColor = (priority) => ({
    'Critical': 'border-l-red-600',
    'High':     'border-l-orange-500',
    'Medium':   'border-l-blue-400',
    'Low':      'border-l-gray-300',
  }[priority] || 'border-l-gray-300');

  const allStatuses = ['All', 'Open', 'In Progress', 'Done'];
  const counts = allStatuses.reduce((acc, s) => {
    acc[s] = s === 'All' ? items.length : items.filter(i => i.status === s).length;
    return acc;
  }, {});

  const visible = filter === 'All' ? items : items.filter(i => i.status === filter);
  const sorted = [...visible].sort((a, b) => {
    const order = { 'Open': 0, 'In Progress': 1, 'Done': 2 };
    const po = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
    if ((order[a.status] ?? 9) !== (order[b.status] ?? 9)) return (order[a.status] ?? 9) - (order[b.status] ?? 9);
    return (po[a.priority] ?? 9) - (po[b.priority] ?? 9);
  });

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <h3 className="text-xl font-bold mb-6 flex items-center gap-2"><CheckSquare className="text-red-500" /> Punch List</h3>

      {/* Add form */}
      <div className="bg-red-50 p-4 rounded-lg mb-6 border border-red-100">
        <div className="flex flex-col md:flex-row gap-2 mb-2">
          <input className="flex-[2] border rounded p-2 text-sm" placeholder="Issue description *" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAdd()} />
          <input className="flex-1 border rounded p-2 text-sm" placeholder="Location" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} />
        </div>
        <div className="flex flex-col md:flex-row gap-2 mb-2">
          <select className="border rounded p-2 text-sm bg-white" value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <input className="flex-1 border rounded p-2 text-sm" placeholder="Assignee" value={formData.assignee} onChange={e => setFormData({ ...formData, assignee: e.target.value })} />
        </div>
        <textarea className="w-full border rounded p-2 text-sm mb-2" rows="2" placeholder="Notes / comments..." value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
        <button onClick={handleAdd} className="w-full bg-red-600 text-white px-6 py-2 rounded font-bold hover:bg-red-700 text-sm">+ Add Item</button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {allStatuses.map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${filter === s ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}>
            {s} <span className="opacity-60">({counts[s]})</span>
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map(item => {
          const isEditing = editingId === item.id;
          const isDone = item.status === 'Done';

          return (
            <div key={item.id}
              className={`border rounded-lg flex flex-col relative transition-opacity ${isDone ? 'opacity-60' : ''} ${isEditing ? 'border-blue-400 shadow-md' : 'border-l-4 ' + borderColor(item.priority)}`}>

              {isEditing ? (
                /* ── EDIT MODE ── */
                <div className="p-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">Editing</span>
                    <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                  </div>

                  <textarea
                    className="w-full border rounded p-2 text-sm font-semibold resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                    rows="2" autoFocus
                    placeholder="Description *"
                    value={editData.description}
                    onChange={e => setEditData({ ...editData, description: e.target.value })}
                  />

                  <div className="flex gap-2">
                    <input className="flex-1 border rounded p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Location"
                      value={editData.location}
                      onChange={e => setEditData({ ...editData, location: e.target.value })} />
                    <select className="border rounded p-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={editData.priority}
                      onChange={e => setEditData({ ...editData, priority: e.target.value })}>
                      {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>

                  <input className="border rounded p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Assignee"
                    value={editData.assignee}
                    onChange={e => setEditData({ ...editData, assignee: e.target.value })} />

                  <div>
                    <label className="text-xs text-gray-500 font-medium flex items-center gap-1 mb-1">
                      <MessageSquare size={11} /> Notes / Comments
                    </label>
                    <textarea
                      className="w-full border rounded p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                      rows="4"
                      placeholder="Add notes, comments, or instructions..."
                      value={editData.notes}
                      onChange={e => setEditData({ ...editData, notes: e.target.value })}
                    />
                  </div>

                  <div className="flex gap-2 mt-1">
                    <button onClick={() => saveEdit(item)} disabled={saving}
                      className="flex-1 flex items-center justify-center gap-1 bg-blue-600 text-white rounded py-1.5 text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                      <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button onClick={cancelEdit}
                      className="px-3 border border-gray-300 text-gray-600 rounded text-sm hover:bg-gray-50">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* ── VIEW MODE ── */
                <>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    {/* Header row */}
                    <div className="flex justify-between items-start gap-2">
                      <p className={`font-bold text-sm flex-1 ${isDone ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                        {item.description}
                      </p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => startEdit(item)} title="Edit all fields"
                          className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => cycleStatus(item)}
                          title={`Status: ${item.status} → click to advance`}
                          className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${STATUS_STYLE[item.status] || 'bg-gray-100 text-gray-600'}`}>
                          {item.status}
                        </button>
                      </div>
                    </div>

                    {/* Meta tags */}
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500">{item.location || 'No location'}</span>
                      {item.assignee && (
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded flex items-center gap-1">
                          <User size={9} /> {item.assignee}
                        </span>
                      )}
                    </div>

                    {/* Notes */}
                    {item.notes ? (
                      <div className="bg-amber-50 border border-amber-100 rounded p-2 flex gap-1.5">
                        <MessageSquare size={12} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{item.notes}</p>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(item)}
                        className="text-xs text-gray-300 hover:text-blue-400 text-left flex items-center gap-1 py-0.5">
                        <MessageSquare size={11} /> Add notes...
                      </button>
                    )}
                  </div>

                  {/* Photo */}
                  {item.photo_path ? (
                    <div className="relative group cursor-pointer h-28 mx-3 mb-2 rounded overflow-hidden"
                      onClick={() => setPreviewFile({ name: 'Snag Photo', path: item.photo_path, type: 'image/jpeg' })}>
                      <img src={getUrl(item.photo_path)} alt="Proof" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <Maximize2 className="text-white opacity-0 group-hover:opacity-100" size={20} />
                      </div>
                    </div>
                  ) : (
                    <label className="mx-3 mb-2 border-2 border-dashed border-gray-200 rounded h-20 flex flex-col items-center justify-center text-gray-300 cursor-pointer hover:border-blue-400 hover:text-blue-400 transition-colors">
                      <Camera size={20} />
                      <span className="text-xs mt-1">{uploading === item.id ? 'Uploading...' : 'Add Photo'}</span>
                      <input type="file" className="hidden" accept="image/*" capture="environment"
                        onChange={(e) => handlePhotoUpload(e, item.id)} disabled={uploading !== null} />
                    </label>
                  )}

                  {/* Footer */}
                  <div className="flex justify-between items-center px-3 py-2 border-t border-gray-100">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      item.priority === 'Critical' ? 'text-red-600' :
                      item.priority === 'High' ? 'text-orange-500' : 'text-gray-400'}`}>
                      {item.priority}
                    </span>
                    <button onClick={() => handleDelete(item)} className="text-gray-300 hover:text-red-500 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {sorted.length === 0 && (
          <div className="col-span-full py-16 text-center border-2 border-dashed border-gray-100 rounded-lg">
            <CheckSquare size={32} className="text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">{filter === 'All' ? 'No punch list items yet.' : `No "${filter}" items.`}</p>
          </div>
        )}
      </div>

      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};
export default PunchListView;
